import { __ } from "@wordpress/i18n";
import { useState, useEffect } from "@wordpress/element";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { css } from "@/utils/css";
import { api } from "@/config/api-fetch";
import { Spinner, Tooltip } from "@wordpress/components";
import { 
    FaLayerGroup, 
    FaSearch, 
    FaCalendarAlt, 
    FaThumbsUp, 
    FaThumbsDown,
    FaPercentage,
    FaClock,
    FaChartLine,
    FaComments,
    FaQuestion,
    FaCheckCircle,
    FaTimesCircle,
    FaArrowRight,
    FaCrown,
    FaExclamationTriangle
} from "react-icons/fa";
import { useRoute } from "@/hooks/use-route";
import { useSearchAnalytics } from "@/hooks/use-search-analytics";
import { useUsageStats } from "@/hooks/use-usage-stats";
import { IS_PRO, BUY_PRO_URL } from "@/utils/constants";



// Helper to parse date from backend (assumes UTC if no timezone specified)
const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/\-\d{2}:\d{2}$/)) {
        return parseISO(dateString + 'Z');
    }
    return parseISO(dateString);
};

type StatCardProps = {
    label: string;
    value: number | string;
    icon: React.ReactNode;
    gradient: string;
    description?: string;
    trend?: { value: number; isPositive: boolean };
};

function StatCard({ label, value, icon, gradient, description, trend }: StatCardProps) {
    return (
        <div className={css("analytics-stat-card")} style={{ background: gradient }}>
            <div className={css("asc-icon-wrapper")}>
                {icon}
            </div>
            <div className={css("asc-content")}>
                <div className={css("asc-value")}>{value}</div>
                <div className={css("asc-label")}>{label}</div>
                {description && <div className={css("asc-description")}>{description}</div>}
            </div>
            {trend && (
                <div className={css("asc-trend", trend.isPositive ? "positive" : "negative")}>
                    {trend.isPositive ? "↑" : "↓"} {trend.value}%
                </div>
            )}
        </div>
    );
}

type UsageLimitCardProps = {
    current: number;
    limit: number;
};

function UsageLimitCard({ current, limit }: UsageLimitCardProps) {
    const percentage = Math.min(Math.round((current / limit) * 100), 100);
    const isExceeded = current >= limit;
    const isWarning = percentage >= 80 && !isExceeded;
    
    // Dynamic gradient based on usage
    const getGradient = () => {
        if (isExceeded) return "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
        if (isWarning) return "linear-gradient(135deg, #f97316 0%, #ea580c 100%)";
        return "linear-gradient(135deg, #10b981 0%, #059669 100%)";
    };

    return (
        <div className={css("analytics-stat-card", "usage-limit-card")} style={{ background: getGradient() }}>
            <div className={css("asc-icon-wrapper")}>
                {isExceeded ? <FaExclamationTriangle size={20} /> : <FaCrown size={20} />}
            </div>
            <div className={css("asc-content")}>
                <div className={css("asc-value")}>
                    {current} / {limit}
                </div>
                <div className={css("asc-label")}>
                    {isExceeded ? __("Limit Reached", "antimanual") : __("Usage Limit", "antimanual")}
                </div>
                {/* Progress Bar */}
                <div className={css("usage-progress-wrapper")}>
                    <div className={css("usage-progress-bar")}>
                        <div 
                            className={css("usage-progress-fill")}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    <span className={css("usage-progress-text")}>{percentage}%</span>
                </div>
                {isExceeded && (
                    <a 
                        href={BUY_PRO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css("usage-upgrade-link")}
                    >
                        {__("Upgrade to Pro", "antimanual")} →
                    </a>
                )}
            </div>
        </div>
    );
}

function SatisfactionRing({ percentage, votes }: { percentage: number; votes: number }) {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
        <div className={css("satisfaction-ring")}>
            <svg viewBox="0 0 100 100" className={css("sr-svg")}>
                <circle
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="8"
                />
                <circle
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke={percentage >= 50 ? "#22c55e" : "#f59e0b"}
                    strokeWidth="8"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                />
            </svg>
            <div className={css("sr-content")}>
                <div className={css("sr-value")}>{percentage}%</div>
                <div className={css("sr-label")}>{__("Satisfaction", "antimanual")}</div>
            </div>
            <div className={css("sr-votes")}>
                {votes > 0 ? `${votes} ${__("total votes", "antimanual")}` : __("No votes yet", "antimanual")}
            </div>
        </div>
    );
}

export default function Analytics() {
    const { data: analytics, isLoading: loading, error: queryError } = useSearchAnalytics();
    const { data: usageStats } = useUsageStats();
    const { navigate, query } = useRoute();
    
    // Derived error message
    const error = queryError ? (queryError instanceof Error ? queryError.message : __("Failed to load analytics", "antimanual")) : null;

    if (loading) {
        return (
            <div className={css("analytics-loading-enhanced")}>
                <div className={css("ale-spinner")}>
                    <Spinner />
                </div>
                <h4>{__("Loading analytics...", "antimanual")}</h4>
                <p>{__("Fetching your search performance data", "antimanual")}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={css("analytics-error-enhanced")}>
                <div className={css("aee-icon")}>
                    <FaTimesCircle size={32} />
                </div>
                <h4>{__("Failed to load analytics", "antimanual")}</h4>
                <p>{error}</p>
                <button 
                    className={css("aee-retry-btn")}
                    onClick={() => window.location.reload()}
                >
                    {__("Try Again", "antimanual")}
                </button>
            </div>
        );
    }

    const totalVotes = (analytics?.helpful_votes || 0) + (analytics?.unhelpful_votes || 0);
    const helpfulPercentage = totalVotes > 0
        ? Math.round((analytics!.helpful_votes / totalVotes) * 100)
        : 0;

    const goToSearchQueries = () => {
        navigate(`?page=${query.page}&tab=search-queries`);
    };

    return (
        <div className={css("analytics-enhanced")}>
            {/* Performance Overview */}
            <section className={css("ae-section")}>
                <div className={css("ae-section-header")}>
                    <div className={css("ae-section-icon")}>
                        <FaChartLine size={16} />
                    </div>
                    <h3>{__("Performance Overview", "antimanual")}</h3>
                </div>
                <div className={css("ae-stats-grid")}>
                    <StatCard
                        label={__("Active Blocks", "antimanual")}
                        value={analytics?.block_count || 0}
                        gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                        description={__("Search blocks on your site", "antimanual")}
                        icon={<FaLayerGroup size={20} />}
                    />
                    <StatCard
                        label={__("Total Queries", "antimanual")}
                        value={analytics?.total_queries || 0}
                        gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
                        description={__("All-time searches", "antimanual")}
                        icon={<FaSearch size={20} />}
                    />
                    <StatCard
                        label={__("This Month", "antimanual")}
                        value={analytics?.monthly_queries || 0}
                        gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        description={__("Monthly queries", "antimanual")}
                        icon={<FaCalendarAlt size={20} />}
                    />
                    {/* Usage Limit Card - Only for free users */}
                    {!IS_PRO && usageStats?.search_block && (
                        <UsageLimitCard 
                            current={usageStats.search_block.count}
                            limit={usageStats.search_block.limit}
                        />
                    )}
                </div>
            </section>

            {/* Two Column Layout */}
            <div className={css("ae-two-col")}>
                {/* User Feedback Section */}
                <section className={css("ae-section")}>
                    <div className={css("ae-section-header")}>
                        <div className={css("ae-section-icon")}>
                            <FaComments size={16} />
                        </div>
                        <h3>{__("User Feedback", "antimanual")}</h3>
                    </div>
                    
                    <div className={css("ae-feedback-container")}>
                        <div className={css("ae-feedback-stats")}>
                            <div className={css("ae-feedback-card", "helpful")}>
                                <div className={css("afc-icon")}>
                                    <FaThumbsUp size={18} />
                                </div>
                                <div className={css("afc-content")}>
                                    <div className={css("afc-value")}>{analytics?.helpful_votes || 0}</div>
                                    <div className={css("afc-label")}>{__("Helpful", "antimanual")}</div>
                                </div>
                            </div>
                            <div className={css("ae-feedback-card", "unhelpful")}>
                                <div className={css("afc-icon")}>
                                    <FaThumbsDown size={18} />
                                </div>
                                <div className={css("afc-content")}>
                                    <div className={css("afc-value")}>{analytics?.unhelpful_votes || 0}</div>
                                    <div className={css("afc-label")}>{__("Not Helpful", "antimanual")}</div>
                                </div>
                            </div>
                        </div>
                        <SatisfactionRing percentage={helpfulPercentage} votes={totalVotes} />
                    </div>

                    {analytics?.block_count === 0 && (
                        <div className={css("ae-empty-notice")}>
                            <FaSearch size={24} />
                            <div>
                                <strong>{__("No Search Blocks Found", "antimanual")}</strong>
                                <p>{__("Add an AI Search block to start tracking analytics.", "antimanual")}</p>
                            </div>
                        </div>
                    )}
                </section>

                {/* Recent Queries Section */}
                <section className={css("ae-section")}>
                    <div className={css("ae-section-header")}>
                        <div className={css("ae-section-icon")}>
                            <FaClock size={16} />
                        </div>
                        <h3>{__("Recent Queries", "antimanual")}</h3>
                        {analytics?.recent_queries && analytics.recent_queries.length > 0 && (
                            <button 
                                className={css("ae-view-all-btn")}
                                onClick={goToSearchQueries}
                            >
                                {__("View All", "antimanual")} <FaArrowRight size={10} />
                            </button>
                        )}
                    </div>
                    
                    <div className={css("ae-queries-list")}>
                        {analytics?.recent_queries && analytics.recent_queries.length > 0 ? (
                            analytics.recent_queries.map((q, i) => (
                                <div key={i} className={css("ae-query-item")}>
                                    <div className={css("aeq-icon")}>
                                        <FaSearch size={12} />
                                    </div>
                                    <div className={css("aeq-content")} style={{ minWidth: 0 }}>
                                        {q.query ? (
                                            <Tooltip text={q.query}>
                                                <div className={css("aeq-text")} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "default" }}>
                                                    {q.query}
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <div className={css("aeq-text")}>
                                                <span style={{ fontStyle: "italic", opacity: 0.7 }}>
                                                    {__("(No query)", "antimanual")}
                                                </span>
                                            </div>
                                        )}
                                        <div className={css("aeq-time")}>
                                            <FaClock size={10} />
                                            {q.created_at ? formatDistanceToNow(parseDate(q.created_at), { addSuffix: true }) : ""}
                                        </div>
                                    </div>
                                    <div className={css("aeq-status", q.is_helpful === null ? "neutral" : (q.is_helpful ? "helpful" : "unhelpful"))}>
                                        {q.is_helpful === null ? (
                                            <FaQuestion size={10} />
                                        ) : q.is_helpful ? (
                                            <FaCheckCircle size={10} />
                                        ) : (
                                            <FaTimesCircle size={10} />
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={css("ae-queries-empty")}>
                                <FaSearch size={24} />
                                <p>{__("No recent queries yet", "antimanual")}</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
